import AU from "country-flag-icons/react/3x2/AU";
import CA from "country-flag-icons/react/3x2/CA";
import EU from "country-flag-icons/react/3x2/EU";
import GB from "country-flag-icons/react/3x2/GB";
import US from "country-flag-icons/react/3x2/US";

import { cn } from "@/lib/utils";

// Taken from the import rather than written out. The package types its flags
// against an `HTMLElement & SVGElement` intersection and does not export the
// alias, so any hand-written `ComponentType<SVGProps<...>>` signature is a near
// miss that fails to assign.
type FlagComponent = typeof US;

/**
 * Which country's flag stands for a currency.
 *
 * Imported one flag at a time rather than by code at runtime. The subpath is
 * statically analyzable, so the bundle carries exactly the two flags named here
 * instead of every flag in the world -- which is the reason this package was
 * chosen over `react-world-flags`, whose lookup-by-string forces the whole set
 * in and drags a 20MB country dataset along with it.
 *
 * A currency is not a country, so this is a deliberate editorial mapping rather
 * than a derivation. Adding a currency is one import and one line, and the
 * fallback below means forgetting the line degrades to a bare code rather than
 * crashing a page that shows money.
 */
const FLAG_BY_CURRENCY: Record<string, FlagComponent> = {
  USD: US,
  CAD: CA,
  // The euro has no country, so the EU flag is the only honest mark for it.
  EUR: EU,
  // GB, not UK: ISO 3166 uses GB, and `country-flag-icons` keys off ISO.
  GBP: GB,
  AUD: AU,
};

interface CurrencyFlagProps {
  /** ISO 4217 code, e.g. `USD`. Case-insensitive. */
  currency: string | null | undefined;
  className?: string;
}

/**
 * The flag for a reporting currency.
 *
 * Deliberately **not** rendered next to amounts. A flag on every figure in a
 * dense table is noise, and `Intl` already distinguishes `$1,234.50` from
 * `CA$1,234.50` on its own. This exists for the one place the active currency
 * is declared -- the sidebar control beside Settings -- and for the rows of the
 * picker it opens.
 *
 * Returns `null` for an unmapped or missing currency. Callers always render the
 * code as well, so the label stays correct and complete with no flag; showing
 * the wrong flag would be a wrong claim about the money.
 */
export function CurrencyFlag({ currency, className }: CurrencyFlagProps) {
  const code = (currency || "").trim().toUpperCase();
  const Flag = FLAG_BY_CURRENCY[code];
  if (!Flag) return null;

  return (
    <Flag
      // The hairline ring keeps a flag with white in it (both of these) from
      // dissolving into a light theme's surface. On the foreground token, so
      // it inverts with the theme instead of vanishing on a dark one.
      className={cn(
        "h-3.5 w-[1.3125rem] shrink-0 rounded-[2px] ring-1 ring-inset ring-foreground/10",
        className,
      )}
      // The code beside it already names the currency, so announcing the
      // country here would read the row twice and name a country the user did
      // not choose.
      aria-hidden
    />
  );
}
